import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './index.scss';
import { ReactComponent as MagnifyIcon } from '$/assets/icons/MagnifyingGlass.svg';
import * as Bixby from '$/core';
import { Link } from 'react-router-dom';
import debounce from 'lodash/debounce';

interface ISearchBoxProps {
  placeholder: string;
  onChange?: (searchText: string) => void | Promise<void>;
  searchResults?: Bixby.Models.SearchResult[];
  onResultsClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void | Promise<void>;
}

const SearchBox: React.FC<ISearchBoxProps> = props => {
  const { placeholder, onChange, searchResults, onResultsClick } = props;
  const emptyState = useMemo(() => {
    return <div className="empty_state" />;
  }, []);
  const searchAndUpdate = (searchText: string) => {
    if (onChange) {
      onChange(searchText);
    }
  };

  const value = { debounce: debounce(searchAndUpdate, 1000) };
  const [debouncerState] = useState({ value });
  const [resultsHtml, setResultsHtml] = useState(emptyState);
  const [text, setText] = useState({ searchField: '' });
  const [open, setOpen] = useState(false);

  const handleClickInternal = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      setText({ searchField: '' });
      setResultsHtml(emptyState);
      if (onResultsClick) {
        onResultsClick(event);
      }
    },
    [emptyState, onResultsClick]
  );

  const handleChangeInternal = async (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncerState.value.debounce.cancel();
    const searchText = event.target.value;
    setText({ searchField: searchText });
    if (Bixby.Utils.isNullOrEmpty(searchText)) {
      toggleResults(false, emptyState);
      return;
    }
    await debouncerState.value.debounce(searchText);
    setOpen(true);
  };

  const wrapperNode = useRef(null as any);
  const inputNode = useRef(null as any);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (wrapperNode && wrapperNode.current && wrapperNode.current.contains(event.target)) {
        return;
      }
      toggleResults(false);
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const trySetSearchResults = useCallback(() => {
    if (!Bixby.Utils.isNullOrEmpty(text.searchField) && searchResults && Bixby.Utils.isDefined(searchResults)) {
      const html =
        searchResults.length === 0 ? (
          <div>No results found.</div>
        ) : (
          <div>
            {searchResults.map((s, ix) => {
              return s.link ? (
                <div key={ix}>
                  <Link onClick={handleClickInternal} to={s.link}>
                    {s.result}
                  </Link>
                </div>
              ) : (
                <div key={ix}>{s.result}</div>
              );
            })}
          </div>
        );
      toggleResults(true, html);
    } else {
      toggleResults(false, emptyState);
    }
  }, [emptyState, handleClickInternal, searchResults, text.searchField]);

  const toggleResults = (shouldOpen: boolean, html?: any) => {
    if (html) {
      setResultsHtml(html);
    }
    setOpen(shouldOpen);
  };

  const handleFocus = () => {
    trySetSearchResults();
  };

  useEffect(() => {
    trySetSearchResults();
  }, [searchResults, trySetSearchResults]);

  return (
    <div ref={wrapperNode} className="search_box_wrapper">
      <div className="search_input_wrapper">
        <input
          onFocus={handleFocus}
          ref={inputNode}
          value={text.searchField}
          type="text"
          placeholder={placeholder}
          onChange={handleChangeInternal}
        />
        <MagnifyIcon />
      </div>
      {open && <div className="search_results">{resultsHtml}</div>}
    </div>
  );
};

export default SearchBox;
